import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HighlightedText } from './HighlightedText';
import { useQuestionHighlights } from '@/features/quiz/hooks/useQuestionHighlights';
import type { MarkKind } from '@/features/quiz/utils/highlightRanges';
import { snapToWordBoundaries } from '@/features/quiz/utils/highlightRanges';

interface QuestionStemHighlighterProps {
  questionId: string;
  text: string;
  /** Narration passthrough — unchanged behavior, see HighlightedText. */
  token: number | null;
}

interface PendingSelection {
  start: number;
  end: number;
  /** Viewport coords for the floating marker controls (from Range.getBoundingClientRect). */
  top: number;
  left: number;
}

/**
 * Counts plain-text characters inside `root` up to (`target`, `targetOffset`)
 * from a Selection Range endpoint — i.e. "how far into root's rendered text
 * does this point fall". Building a Range from the start of `root` to that
 * point and reading its stringified length is the standard trick for this
 * (it's how contenteditable caret-offset libraries do it) and correctly
 * handles both text-node and element-node endpoints without walking the tree
 * by hand. It stays correct no matter how many <mark>/<span> wrappers
 * HighlightedText's narration/marking renders in between — the wrappers
 * never add or remove characters.
 */
function textOffsetWithin(root: Node, target: Node, targetOffset: number): number {
  const preRange = document.createRange();
  preRange.selectNodeContents(root);
  preRange.setEnd(target, targetOffset);
  return preRange.toString().length;
}

/**
 * Lets a user select text inside the question stem and mark it, ASWB/
 * Prometric-marker style. Selecting text (mouse drag, keyboard shift+arrow,
 * or a mobile long-press-drag) surfaces "Highlight" and "Strike" buttons;
 * clicking one toggles that mark for the (word-snapped) span via
 * `useQuestionHighlights`. Native text selection and copy are untouched —
 * this only listens, it never intercepts the selection.
 */
export const QuestionStemHighlighter: React.FC<QuestionStemHighlighterProps> = ({
  questionId,
  text,
  token,
}) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const highlights = useQuestionHighlights(questionId, 'highlight');
  const strikes = useQuestionHighlights(questionId, 'strike');
  const [pending, setPending] = useState<PendingSelection | null>(null);

  const handleSelectionChange = useCallback(() => {
    const container = containerRef.current;
    const selection = typeof window !== 'undefined' ? window.getSelection() : null;
    if (!container || !selection || selection.rangeCount === 0 || selection.isCollapsed) {
      setPending(null);
      return;
    }

    const range = selection.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) {
      setPending(null);
      return;
    }

    const rawStart = textOffsetWithin(container, range.startContainer, range.startOffset);
    const rawEnd = textOffsetWithin(container, range.endContainer, range.endOffset);
    const lo = Math.min(rawStart, rawEnd);
    const hi = Math.max(rawStart, rawEnd);
    if (lo === hi) {
      setPending(null);
      return;
    }

    const snapped = snapToWordBoundaries(text, lo, hi);
    const rect = range.getBoundingClientRect();
    setPending({ start: snapped.start, end: snapped.end, top: rect.top, left: rect.left + rect.width / 2 });
  }, [text]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const hideOnScroll = () => setPending(null);
    document.addEventListener('selectionchange', handleSelectionChange);
    // A scroll moves the text out from under a viewport-anchored button —
    // hide it rather than let it drift. Re-selecting brings it back.
    window.addEventListener('scroll', hideOnScroll, true);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      window.removeEventListener('scroll', hideOnScroll, true);
    };
  }, [handleSelectionChange]);

  const applyMark = (kind: MarkKind) => {
    if (!pending) return;
    const selection = { start: pending.start, end: pending.end };
    // A span is highlighted or struck, never both: marking one kind takes the
    // selection back off the other.
    const [chosen, other] = kind === 'highlight' ? [highlights, strikes] : [strikes, highlights];
    chosen.toggle(selection);
    other.clear(selection);
    setPending(null);
    window.getSelection()?.removeAllRanges();
  };

  return (
    <span ref={containerRef} className="relative">
      <HighlightedText
        text={text}
        token={token}
        markedRanges={highlights.ranges}
        struckRanges={strikes.ranges}
      />
      {pending && (
        <span
          // Prevent the controls' own mousedown from collapsing the text
          // selection before onClick fires — the offsets are already
          // captured in `pending`, but a vanishing button on mousedown would
          // still eat the click.
          onMouseDown={(e) => e.preventDefault()}
          style={{
            position: 'fixed',
            top: Math.max(pending.top - 44, 8),
            left: pending.left,
            transform: 'translateX(-50%)',
          }}
          className="z-30 inline-flex items-center gap-1 rounded-full bg-neutral-900 px-1 py-1 shadow-lg"
        >
          <button
            type="button"
            onClick={() => applyMark('highlight')}
            className="rounded-full px-3 py-0.5 text-xs font-bold text-white"
          >
            Highlight
          </button>
          <button
            type="button"
            onClick={() => applyMark('strike')}
            className="rounded-full px-3 py-0.5 text-xs font-bold text-white line-through"
          >
            Strike
          </button>
        </span>
      )}
    </span>
  );
};
